// External packages
import * as ReactAria from 'react-aria-components';
import * as ReactHookForm from 'react-hook-form';
import * as React from 'react';

// Components
import { Popover, SelectTriggerButton, Option } from 'components/Select';
import * as Forms from 'components/Forms';
import { Icon } from 'components/Icons';

// Utils
import { twMerge } from 'tailwind-merge';

/* SelectMultiple */

export type SelectMultipleProps<T extends object> = Omit<
  ReactAria.ListBoxProps<T>,
  'children'
> & {
  children: React.ReactNode | ((item: T) => React.ReactNode);
  placeholder?: string;
  name?: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  clearable?: boolean;
};

export const SelectMultiple = <T extends object>({
  placeholder,
  isInvalid,
  isDisabled,
  className,
  children,
  clearable = false,
  ...props
}: SelectMultipleProps<T>) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const [triggerWidth, setTriggerWidth] = React.useState<number>();

  React.useEffect(() => {
    if (buttonRef.current) {
      setTriggerWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  const selectedKeys =
    props.selectedKeys && typeof props.selectedKeys !== 'string'
      ? Array.from(props.selectedKeys)
      : [];
  const hasSelection = selectedKeys.length > 0;

  const renderChildren = () => (
    <>
      {clearable && hasSelection && (
        <Option
          key="__clear__"
          id="__clear__"
          textValue="Clear selection"
          className="text-gray-400"
        >
          <span>Clear selection</span>
        </Option>
      )}
      {typeof children === 'function' && props.items
        ? Array.from(props.items).map(children)
        : children}
    </>
  );

  const handleSelectionChange = (value: any) => {
    if (value instanceof Set && value.has('__clear__')) {
      if (props.onSelectionChange) props.onSelectionChange(new Set());
    } else if (props.onSelectionChange) {
      props.onSelectionChange(value);
    }
  };

  return (
    <ReactAria.DialogTrigger>
      <SelectTriggerButton
        ref={buttonRef}
        data-invalid={isInvalid}
        isDisabled={isDisabled}
        className={twMerge(
          'data-[invalid=true]:border-red-600 data-[invalid=true]:text-red-600 data-[invalid=true]:placeholder:text-red-600 data-[invalid=true]:hover:border-red-800',
          className as string
        )}
      >
        <span
          style={{ maxWidth: triggerWidth ? triggerWidth - 62 : 'auto' }}
          className="truncate"
        >
          {triggerWidth && selectedKeys.length > 0
            ? selectedKeys.join(', ')
            : placeholder}
        </span>
        <Icon
          name="chevron-down"
          aria-hidden="true"
          className="pointer-events-none size-5"
        />
      </SelectTriggerButton>
      <Popover
        maxHeight={220}
        placement="bottom right"
        style={{ minWidth: triggerWidth }}
        className="transition-[min-width]"
      >
        <div
          onKeyDownCapture={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
        >
          <ReactAria.ListBox
            {...props}
            selectionMode="multiple"
            onSelectionChange={handleSelectionChange}
          >
            {renderChildren()}
          </ReactAria.ListBox>
        </div>
      </Popover>
    </ReactAria.DialogTrigger>
  );
};

/* SelectMultipleField */

export type SelectMultipleFieldProps<T extends object> =
  SelectMultipleProps<T> & {
    subLabelProps?: Forms.SubLabelProps;
    wrapperClassName?: string;
    label?: string;
    labelProps?: Forms.LabelProps;
  };

export const SelectMultipleField: React.FC<SelectMultipleFieldProps<any>> = ({
  name,
  isInvalid,
  onSelectionChange,
  onBlur,
  label,
  labelProps,
  subLabelProps,
  wrapperClassName,
  children,
  clearable = false,
  ...props
}) => {
  const { field, fieldState } = ReactHookForm.useController<
    { __name__: string },
    '__name__'
  >({ name: name as '__name__' });

  const selectedKeys = Array.isArray(field.value)
    ? field.value
    : field.value
      ? Array.from(field.value)
      : [];
  const hasSelection = selectedKeys.length > 0;

  const renderChildren = () => (
    <>
      {clearable && hasSelection && (
        <Option
          key="__clear__"
          id="__clear__"
          textValue="Clear selection"
          className="text-gray-400"
        >
          <span>Clear selection</span>
        </Option>
      )}
      {typeof children === 'function' && props.items
        ? Array.from(props.items).map(children)
        : children}
    </>
  );

  const handleSelectionChange = (value: any) => {
    if (value instanceof Set && value.has('__clear__')) {
      field.onChange([]);
      if (typeof onSelectionChange === 'function') {
        (onSelectionChange as any)([]);
      }
    } else {
      const arr =
        value instanceof Set
          ? Array.from(value)
          : Array.isArray(value)
            ? value
            : value
              ? [value]
              : [];
      field.onChange(arr);
      if (typeof onSelectionChange === 'function') {
        (onSelectionChange as any)(arr);
      }
    }
  };

  return (
    <div className={wrapperClassName}>
      {label && (
        <Forms.Label {...labelProps} htmlFor={name}>
          {label}
        </Forms.Label>
      )}
      <SelectMultiple
        {...field}
        {...props}
        id={name}
        selectedKeys={selectedKeys}
        isInvalid={Boolean(fieldState.error) ?? isInvalid}
        onSelectionChange={handleSelectionChange}
        onBlur={(event) => {
          field.onBlur();
          if (onBlur) {
            onBlur(event);
          }
        }}
      >
        {renderChildren()}
      </SelectMultiple>
      {fieldState.error && (
        <Forms.SubLabel
          {...subLabelProps}
          role="alert"
          slot="errorMessage"
          colorScheme="error"
        >
          {fieldState.error.message}
        </Forms.SubLabel>
      )}
    </div>
  );
};
